import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '@govern360/shared';

/* Landing page for marketplace purchases from any cloud.
   Route: /marketplace/landing?handoff=<uuid>
   The provider token never reaches this page - marketplace-ingress resolved it server side.

   Deliberately does not use lib/netlifyFn.callFn: that helper throws on non-2xx and
   discards the status code, and this page must tell 401 (offer sign-in, keep the handoff)
   apart from a real failure. Same session, same bearer token, one extra signal. */

const PROVIDER_LABEL = { azure: 'Microsoft Marketplace', aws: 'AWS Marketplace', gcp: 'Google Cloud Marketplace' };

const ERROR_COPY = {
  no_token: 'This link did not carry a purchase reference. Open the subscription from your cloud provider console and choose the setup or configure option again.',
  token_invalid_or_expired: 'This purchase link has expired. Open the subscription from your cloud provider console and choose the setup or configure option again.',
  provider_not_configured: 'This marketplace is not available yet. Email support@aivons.com.',
  handoff_expired: 'This setup link has expired or was already used. Reopen the subscription from your cloud provider console.'
};

async function callFunction(name, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`/.netlify/functions/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || ''}`
    },
    body: JSON.stringify(payload || {})
  });
  const json = await res.json().catch(() => ({}));
  return { status: res.status, json };
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-slate-200 py-2 text-sm last:border-b-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

export default function MarketplaceLandingPage() {
  const [phase, setPhase] = useState('loading');   // loading | signin | review | working | done | error
  const [message, setMessage] = useState('');
  const [data, setData] = useState(null);
  const [handoff, setHandoff] = useState('');

  const preview = useCallback(async (id) => {
    setPhase('loading');
    const r = await callFunction('marketplace-claim', { handoff: id, preview: true });
    if (r.status === 401) {
      // App.jsx gates this route, so a 401 here means the session expired mid-flow.
      setPhase('error');
      setMessage('Your session expired. Sign in again and reopen the setup link from your cloud provider console.');
      return;
    }
    if (r.status !== 200 || !r.json || !r.json.ok) {
      setPhase('error');
      setMessage(ERROR_COPY[(r.json && r.json.code) || ''] || 'Govern360 could not read this purchase. Email support@aivons.com.');
      return;
    }
    setData(r.json);
    setPhase('review');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) {
      setPhase('error');
      setMessage(ERROR_COPY[err] || 'Setup could not start. Email support@aivons.com and quote: ' + err);
      return;
    }
    const id = params.get('handoff') || sessionStorage.getItem('g360_marketplace_handoff');
    if (!id) {
      setPhase('error');
      setMessage(ERROR_COPY.no_token);
      return;
    }
    setHandoff(id);
    preview(id);
  }, [preview]);

  async function finish() {
    setPhase('working');
    const r = await callFunction('marketplace-claim', { handoff });
    if (r.status !== 200 || !r.json || !r.json.ok) {
      setPhase('error');
      setMessage((r.json && r.json.error) || 'Setup did not complete. Nothing was charged. Email support@aivons.com.');
      return;
    }
    setData(Object.assign({}, data, { result: r.json }));
    setPhase('done');
  }



  const ent = data && data.entitlement ? data.entitlement : null;
  const providerLabel = data && data.provider ? (PROVIDER_LABEL[data.provider] || data.provider) : 'Marketplace';

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{providerLabel}</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Set up your Govern360 subscription</h1>
      </div>

      {phase === 'loading' && <p className="text-sm text-slate-600">Checking your purchase...</p>}


      {phase === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <p className="text-sm font-medium text-red-800">[ALERT] {message}</p>
        </div>
      )}

      {(phase === 'review' || phase === 'working') && data && (
        <div className="space-y-6">
          {ent && ent.isTest && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              [WARNING] This is a test purchase from a preview listing. It is not a production subscription.
            </div>
          )}

          {ent ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Your purchase</h2>
              <Row label="Plan" value={ent.planCode || ent.providerPlanCode} />
              <Row label="Seats" value={ent.quantity} />
              <Row label="Billing term" value={ent.termUnit === 'P1Y' ? 'Annual' : ent.termUnit === 'P1M' ? 'Monthly' : ent.termUnit} />
              <Row label="Free trial" value={ent.isTrial ? 'Yes' : 'No'} />
              <Row label="Status with provider" value={ent.providerStatus} />
              {!ent.planCode && (
                <p className="mt-3 text-sm text-amber-800">
                  [WARNING] This plan is not yet mapped to a Govern360 tier. Setup will complete, but
                  Govern360 will not grant access until the mapping exists. Email support@aivons.com.
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">
              Your account is ready to link. The plan details arrive from the provider shortly after setup.
            </div>
          )}

          {data.boundElsewhere ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800">
              [ALERT] This purchase is already linked to a different Govern360 organization.
              Email support@aivons.com and include the subscription reference.
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Link to your organization</h2>
              <p className="text-sm text-slate-600">
                This purchase will be linked to{' '}
                <span className="font-medium text-slate-900">
                  {data.organization && data.organization.name ? data.organization.name : 'your current organization'}
                </span>
                . If that is not the right organization, switch organizations first, then reopen this link.
              </p>
              <button type="button" disabled={phase === 'working'} onClick={finish}
                className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50">
                {phase === 'working' ? 'Finishing setup...' : 'Finish setup'}
              </button>
            </div>
          )}
        </div>
      )}

      {phase === 'done' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
            <p className="text-sm font-medium text-emerald-900">
              [OK] Purchase linked to your organization.
            </p>
            {data.result && data.result.awaitingProvider && (
              <p className="mt-2 text-sm text-emerald-900">
                Your provider is still confirming the subscription. Govern360 will enable the plan
                automatically when that confirmation arrives - usually within a few minutes.
              </p>
            )}
          </div>
          <a href="/" className="inline-block rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">
            Go to the console
          </a>
        </div>
      )}
    </div>
  );
}